import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { formatNumberInput, parseDigits, todayISO } from "../lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const EMPTY = {
  date: "",
  transaction_number: "",
  description: "",
  category: "",
  party: "",
  amount: 0,
  notes: "",
  hidden: false,
};

export default function TransactionModal({ open, onOpenChange, type, transaction, onSaved, forceHidden = false }) {
  const isEdit = Boolean(transaction);
  const { hasPerm } = useAuth();
  const txType = transaction?.type || type || "income";
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [book, setBook] = useState("resmi");

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      transaction
        ? {
            date: transaction.date,
            transaction_number: transaction.transaction_number || "",
            description: transaction.description || "",
            category: transaction.category || "",
            party: transaction.party || "",
            amount: transaction.amount || 0,
            notes: transaction.notes || "",
            hidden: Boolean(transaction.hidden),
          }
        : { ...EMPTY, date: todayISO(), hidden: forceHidden }
    );
    setBook(transaction?.book || (forceHidden ? "imf" : "resmi"));
    api
      .get(`/categories?type=${txType}`)
      .then((r) => setCategories(r.data.filter((c) => c.active)))
      .catch(() => setCategories([]));
  }, [open, transaction, txType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.date) e.date = "Tanggal wajib diisi";
    if (!form.description.trim()) e.description = "Uraian wajib diisi";
    if (!form.amount || form.amount <= 0) e.amount = "Nominal harus lebih besar dari 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, type: txType, hidden: txType === "expense" ? Boolean(form.hidden) : false, book: form.hidden ? "imf" : book };
      if (isEdit) await api.put(`/transactions/${transaction.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(isEdit ? "Transaksi berhasil diperbarui" : "Transaksi berhasil ditambahkan");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const bookOptions = [];
  if (!isEdit) {
    if (txType === "income") {
      if (hasPerm("create_income")) bookOptions.push("resmi");
      if (hasPerm("create_income_imf")) bookOptions.push("imf");
    } else {
      if (hasPerm("create_expense")) bookOptions.push("resmi");
      if (hasPerm("create_expense_imf") || hasPerm("create_special_expense")) bookOptions.push("imf");
    }
  }

  const title = isEdit
    ? "Edit Transaksi"
    : txType === "income"
      ? "Tambah Pemasukan"
      : "Tambah Pengeluaran";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-transaction-form" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {txType === "income"
              ? "Pemasukan akan otomatis menambah saldo kas."
              : "Pengeluaran akan otomatis mengurangi saldo kas."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="tx-date">Tanggal *</Label>
            <Input
              id="tx-date"
              data-testid="input-tx-date"
              type="date"
              value={form.date}
              onChange={(e) => set("date", e.target.value)}
            />
            {errors.date && <p className="text-xs text-rose-400">{errors.date}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tx-number">Kode / No Bukti</Label>
            <Input
              id="tx-number"
              data-testid="input-tx-number"
              placeholder="Otomatis jika kosong"
              value={form.transaction_number}
              onChange={(e) => set("transaction_number", e.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="tx-desc">Uraian *</Label>
            <Input
              id="tx-desc"
              data-testid="input-tx-description"
              placeholder="Contoh: Pembelian ATK kantor"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
            {errors.description && <p className="text-xs text-rose-400">{errors.description}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <select
              data-testid="select-tx-category"
              className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
            >
              <option value="">Pilih kategori</option>
              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tx-party">{txType === "income" ? "Sumber Dana" : "Penerima / Tujuan"}</Label>
            <Input
              id="tx-party"
              data-testid="input-tx-party"
              value={form.party}
              onChange={(e) => set("party", e.target.value)}
            />
          </div>
          {!isEdit && bookOptions.length > 0 && (
            <div className="space-y-1.5">
              <Label>Kas</Label>
              <select
                data-testid="select-tx-book"
                className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                value={form.hidden ? "imf" : book}
                disabled={Boolean(form.hidden)}
                onChange={(e) => setBook(e.target.value)}
              >
                {bookOptions.includes("resmi") && <option value="resmi">Kas Resmi</option>}
                {bookOptions.includes("imf") && <option value="imf">Kas IMF</option>}
              </select>
            </div>
          )}
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="tx-amount">Nominal (Rp) *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-semibold">
                Rp
              </span>
              <Input
                id="tx-amount"
                data-testid="input-tx-amount"
                className="pl-10 font-num"
                inputMode="numeric"
                placeholder="0"
                value={formatNumberInput(form.amount)}
                onChange={(e) => set("amount", parseDigits(e.target.value))}
              />
            </div>
            {errors.amount && <p className="text-xs text-rose-400">{errors.amount}</p>}
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="tx-notes">Keterangan</Label>
            <Textarea
              id="tx-notes"
              data-testid="input-tx-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
          {txType === "expense" && hasPerm("create_special_expense", "edit_special_expense") && (
            <label
              className={`col-span-2 flex items-center gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                form.hidden ? "border-amber-500/30 bg-amber-500/10" : "border-slate-800 bg-[#0F172A] hover:bg-amber-500/10/50"
              }`}
            >
              <input
                type="checkbox"
                data-testid="input-tx-hidden"
                className="accent-amber-600 w-4 h-4"
                checked={Boolean(form.hidden)}
                disabled={forceHidden}
                onChange={(e) => set("hidden", e.target.checked)}
              />
              <EyeOff className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-300 font-medium">
                Sembunyikan dari Dashboard & Laporan — hanya terlihat di halaman Khusus Admin
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="btn-tx-cancel" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button
            data-testid="btn-tx-save"
            className={
              txType === "income"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-slate-900 hover:bg-slate-800 text-white"
            }
            onClick={save}
            disabled={saving}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isEdit ? "Simpan Perubahan" : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
